import type * as AmplitudeTypes from '@amplitude/analytics-browser';
import { Injectable } from '@angular/core';
import { environment } from 'shared/environments/environment';

/**
 * Service for analytics tracking with Amplitude
 * Uses dynamic imports to load only the needed functionality when required
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private amplitude: typeof AmplitudeTypes | null = null;
  private pendingEvents: Array<{
    eventType: string;
    eventProperties?: Record<string, any>;
    userProperties?: Record<string, any>;
  }> = [];
  private pendingUserId: { userId: string | undefined } | null = null;
  private pendingUserProperties: Record<string, any> | null = null;

  /**
   * Initializes Amplitude with the API key
   */
  async init(appVersion: string, defaultTracking: boolean = true): Promise<void> {
    try {
      const amplitudeModule = await import('@amplitude/analytics-browser');
      this.amplitude = amplitudeModule;

      this.amplitude.init(environment.amplitudeKey, {
        defaultTracking,
        appVersion,
        autocapture: {
          pageViews: false,
        },
      });

      // Apply a queued user id first so pending events are attributed to the user
      if (this.pendingUserId) {
        this.setUserId(this.pendingUserId.userId);
        this.pendingUserId = null;
      }

      if (this.pendingUserProperties) {
        const properties = this.pendingUserProperties;
        this.pendingUserProperties = null;
        this.identify(properties);
      }

      // Process any pending events
      this.processPendingEvents();
    } catch (error) {
      console.error('Failed to initialize Amplitude:', error);
    }
  }

  /**
   * Tracks an event with Amplitude
   */
  track(
    eventType: string,
    eventProperties?: Record<string, any>,
    userProperties?: Record<string, any>
  ): void {
    // Queue the event if Amplitude isn't loaded yet
    if (!this.amplitude) {
      this.pendingEvents.push({
        eventType,
        eventProperties,
        userProperties,
      });
      return;
    }

    try {
      if (userProperties) {
        // Use the track method that accepts user_properties
        this.amplitude.track({
          event_type: eventType,
          event_properties: eventProperties,
          user_properties: userProperties,
        });
      } else {
        // Use simpler call if no user properties
        this.amplitude.track(eventType, eventProperties);
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Sets the user ID for Amplitude
   */
  setUserId(userId: string | undefined): void {
    // Queue the identity like pending events — callers may run before init() resolves
    // its dynamic import (e.g. a paywall reading userId from the URL on a fresh load).
    if (!this.amplitude) {
      this.pendingUserId = { userId };
      return;
    }

    try {
      this.amplitude.setUserId(userId);
    } catch (error) {
      console.error('Failed to set user ID:', error);
    }
  }

  /**
   * Gets the device ID from Amplitude
   */
  getDeviceId(): string | undefined {
    if (!this.amplitude) {
      return undefined;
    }

    try {
      return this.amplitude.getDeviceId();
    } catch (error) {
      console.error('Failed to get device ID:', error);
      return undefined;
    }
  }

  /**
   * Identifies a user with Amplitude
   */
  identify(userProperties: Record<string, any>): void {
    // Queue like pending events — the quiz asks its demographic questions on the
    // first screens, which can land before init() resolves its dynamic import.
    if (!this.amplitude) {
      this.pendingUserProperties = { ...this.pendingUserProperties, ...userProperties };
      return;
    }

    try {
      // Create an identify object for user properties
      const identify = new this.amplitude.Identify();

      // Add each user property
      Object.entries(userProperties).forEach(([key, value]) => {
        identify.set(key, value);
      });

      // Send the identify event
      this.amplitude.identify(identify);
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  /**
   * Processes any events that were queued before Amplitude was loaded
   */
  private processPendingEvents(): void {
    if (this.pendingEvents.length > 0) {
      for (const event of this.pendingEvents) {
        this.track(event.eventType, event.eventProperties, event.userProperties);
      }
      this.pendingEvents = [];
    }
  }
}
